"use client";

import { cn } from "@/utilities/ui";
import { decodeHtmlEntities } from "@/utilities/decodeHtmlEntities";
import type { CSSProperties, JSX, ReactNode } from "react";
import { Fragment, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";

type RichTextNode = {
  id?: string;
  type?: string;
  content?: string;
  text?: string;
  format?: number;
  tag?: string;
  /** When set by HeadingClassFeature, overrides the CSS class used for this heading. */
  fontSizeClass?: string;
  listType?: string;
  className?: string;
  style?: CSSProperties;
  children?: RichTextNode[];
  fields?: {
    url?: string;
    linkType?: "custom" | "internal";
    doc?: {
      relationTo?: string;
      value?:
        | string
        | number
        | {
            id?: string | number;
            slug?: string | null;
          }
        | null;
    } | null;
    newTab?: boolean;
    alt?: string;
    mimeType?: string;
    filename?: string;
    width?: number;
    height?: number;
    imageHeight?: "xs" | "sm" | "md" | "lg" | "xl" | "full";
    blockType?: string;
    title?: string;
    webformsUrl?: string;
  };
};

type RichTextProps = {
  data?:
    | ReactNode
    | { richTextNodes: RichTextNode[] }
    | DefaultTypedEditorState;
  content?:
    | ReactNode
    | { richTextNodes: RichTextNode[] }
    | DefaultTypedEditorState;
  enableGutter?: boolean;
  enableProse?: boolean;
  className?: string;
  colorMode?: "light" | "dark";
  defaultSpanDisplay?: "inline" | "block" | "md:block";
};

type RichTextStyle = CSSProperties & {
  "--arrow-right-color"?: string;
};

const hasRichTextNodes = (
  value: unknown,
): value is { richTextNodes: RichTextNode[] } => {
  if (!(typeof value === "object" && value)) return false;
  const maybeNodes = (value as { richTextNodes?: unknown }).richTextNodes;
  return Array.isArray(maybeNodes);
};

const hasLexicalRoot = (
  value: unknown,
): value is { root: { children?: RichTextNode[] } } => {
  if (!(typeof value === "object" && value)) return false;
  const root = (value as { root?: unknown }).root;
  if (!(typeof root === "object" && root)) return false;
  const maybeChildren = (root as { children?: unknown }).children;
  return Array.isArray(maybeChildren);
};

/**
 * RichText.tsx
 * A recursive renderer for rich text nodes with fallback for unknown types.
 */
const PIPEDRIVE_LOADER_SRC = "https://webforms.pipedrive.com/f/loader";

const FORMAT_BOLD = 1;
const FORMAT_ITALIC = 1 << 1;
const FORMAT_STRIKETHROUGH = 1 << 2;
const FORMAT_UNDERLINE = 1 << 3;
const FORMAT_CODE = 1 << 4;
const FORMAT_SUBSCRIPT = 1 << 5;
const FORMAT_SUPERSCRIPT = 1 << 6;

const richTextRootClass =
  "payload-richtext text-base leading-relaxed text-muted-foreground";

const headingClassByTag: Partial<Record<keyof JSX.IntrinsicElements, string>> =
  {
    h1: "heading-xl mt-10 mb-4 text-foreground text-balance first:mt-0",
    h2: "heading-lg mt-10 mb-4 text-foreground text-balance first:mt-0",
    h3: "heading-md mt-8 mb-3 text-foreground text-balance first:mt-0",
    h4: "heading-sm mt-6 mb-3 text-foreground text-balance first:mt-0",
    h5: "text-xl mt-6 mb-2 text-foreground first:mt-0",
    h6: "text-base mt-5 mb-2 text-foreground font-medium uppercase tracking-wide first:mt-0",
  };

const getDefaultSpanClass = (
  display: RichTextProps["defaultSpanDisplay"],
): string => {
  switch (display) {
    case "block":
      return "block";
    case "md:block":
      return "md:block";
    default:
      return "";
  }
};

const formatTextNode = (
  text: string,
  formatValue: number | undefined,
): ReactNode => {
  const decodedText = decodeHtmlEntities(text) ?? text;
  const format = typeof formatValue === "number" ? formatValue : 0;
  let result: ReactNode = decodedText;

  if (format & FORMAT_CODE) {
    result = (
      <code className="rounded-sm bg-muted px-1 py-0.5 text-sm text-foreground">
        {result}
      </code>
    );
  }
  if (format & FORMAT_BOLD) {
    result = <strong className="font-semibold">{result}</strong>;
  }
  if (format & FORMAT_ITALIC) {
    result = <em>{result}</em>;
  }
  if (format & FORMAT_UNDERLINE) {
    result = (
      <span className="underline decoration-1 underline-offset-2">
        {result}
      </span>
    );
  }
  if (format & FORMAT_STRIKETHROUGH) {
    result = <span className="line-through">{result}</span>;
  }
  if (format & FORMAT_SUBSCRIPT) {
    result = <sub>{result}</sub>;
  }
  if (format & FORMAT_SUPERSCRIPT) {
    result = <sup>{result}</sup>;
  }

  return result;
};

const getLinkDocMeta = (fields: RichTextNode["fields"]) => {
  const doc = fields?.doc;
  if (!(typeof doc === "object" && doc)) {
    return {
      relationTo: undefined,
      slug: undefined,
      valueID: undefined,
    };
  }

  const relationTo =
    typeof doc.relationTo === "string" ? doc.relationTo : undefined;
  const value = doc.value;

  if (typeof value === "object" && value) {
    return {
      relationTo,
      slug: typeof value.slug === "string" ? value.slug : undefined,
      valueID:
        typeof value.id === "string" || typeof value.id === "number"
          ? String(value.id)
          : undefined,
    };
  }

  return {
    relationTo,
    slug: undefined,
    valueID:
      typeof value === "string" || typeof value === "number"
        ? String(value)
        : undefined,
  };
};

const getLinkHref = (fields: RichTextNode["fields"]) => {
  if (fields?.linkType !== "internal") {
    return decodeHtmlEntities(fields?.url) ?? fields?.url;
  }

  const { relationTo, slug } = getLinkDocMeta(fields);
  if (!slug) return undefined;

  const normalizedSlug = slug.startsWith("/") ? slug : `/${slug}`;
  return relationTo === "pages"
    ? normalizedSlug
    : relationTo
      ? `/${relationTo}${normalizedSlug}`
      : normalizedSlug;
};

const EmbeddedPipedriveFormNode = ({
  title,
  webformsUrl,
  className,
  style,
}: {
  title?: string;
  webformsUrl?: string;
  className?: string;
  style?: CSSProperties;
}): JSX.Element | null => {
  const normalizedWebformsUrl = useMemo(
    () => webformsUrl?.trim(),
    [webformsUrl],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!normalizedWebformsUrl) return;
    const el = containerRef.current;
    if (!el) return;

    // Follow Pipedrive's embed approach and mount loader inside the host element.
    el.innerHTML = "";

    const script = document.createElement("script");
    script.src = PIPEDRIVE_LOADER_SRC;
    script.async = true;
    el.appendChild(script);
  }, [normalizedWebformsUrl]);

  if (!normalizedWebformsUrl) return null;

  return (
    <div className={cn("my-6", className)} style={style}>
      {title ? <h3 className="mb-4">{title}</h3> : null}
      <div
        ref={containerRef}
        className="pipedriveWebForms w-full"
        data-pd-webforms={normalizedWebformsUrl}
      />
    </div>
  );
};

const renderNode = (
  node: RichTextNode,
  index: number,
  defaultSpanClass: string,
  options?: {
    inListItem?: boolean;
  },
): JSX.Element | null => {
  const getUploadHeightClass = (
    imageHeight?: "xs" | "sm" | "md" | "lg" | "xl" | "full",
  ): string => {
    switch (imageHeight) {
      case "xs":
        return "h-64";
      case "sm":
        return "h-96";
      case "md":
        return "h-[32rem]";
      case "lg":
        return "h-[40rem]";
      case "xl":
        return "h-[48rem]";
      case "full":
        return "h-full";
      default:
        return "h-auto";
    }
  };

  const renderChildren = (childOptions?: { inListItem?: boolean }) =>
    node.children?.map((child, i) =>
      renderNode(child, i, defaultSpanClass, childOptions ?? options),
    );

  switch (node.type) {
    case "linebreak":
      return <br key={index} />;

    case "text": {
      const text = node.content ?? node.text ?? "";
      const formattedText = formatTextNode(text, node.format);

      if (options?.inListItem) {
        return <Fragment key={index}>{formattedText}</Fragment>;
      }

      return (
        <span key={index} className={cn(defaultSpanClass)}>
          {formattedText}
        </span>
      );
    }

    case "paragraph":
      if (options?.inListItem) {
        return <Fragment key={index}>{renderChildren()}</Fragment>;
      }

      return (
        <p
          key={index}
          className={cn("mb-3 text-base text-pretty last:mb-0", node.className)}
          style={node.style}
        >
          {renderChildren()}
        </p>
      );

    case "heading": {
      const rawTag = node.tag ?? "h2";
      const headingMap: Record<string, keyof JSX.IntrinsicElements> = {
        "heading-1": "h1",
        "heading-2": "h2",
        "heading-3": "h3",
        "heading-4": "h4",
        "heading-5": "h5",
        "heading-6": "h6",
      };
      const Tag = (headingMap[rawTag] ?? rawTag) as keyof JSX.IntrinsicElements;
      const headingVariantClass = headingClassByTag[Tag];
      return (
        <Tag
          key={index}
          className={cn(headingVariantClass, node.className)}
          style={node.style}
        >
          {renderChildren() ?? node.content}
        </Tag>
      );
    }

    // "heading-styled" is saved by HeadingClassFeature — same HTML tag as a
    // normal heading, but fontSizeClass overrides which CSS class is applied.
    case "heading-styled": {
      const rawTag = node.tag ?? "h2";
      const headingMap: Record<string, keyof JSX.IntrinsicElements> = {
        "heading-1": "h1",
        "heading-2": "h2",
        "heading-3": "h3",
        "heading-4": "h4",
        "heading-5": "h5",
        "heading-6": "h6",
      };
      const Tag = (headingMap[rawTag] ?? rawTag) as keyof JSX.IntrinsicElements;
      const styleTag = node.fontSizeClass
        ? (node.fontSizeClass as keyof JSX.IntrinsicElements)
        : Tag;
      const headingVariantClass = headingClassByTag[styleTag] ?? headingClassByTag[Tag];
      return (
        <Tag
          key={index}
          className={cn(headingVariantClass, node.className)}
          style={node.style}
        >
          {renderChildren() ?? node.content}
        </Tag>
      );
    }

    case "unordered-list":
      return (
        <ul
          key={index}
          className={cn(
            "my-4 list-disc space-y-1.5 pl-5 marker:text-muted-foreground",
            node.className,
          )}
          style={node.style}
        >
          {renderChildren()}
        </ul>
      );

    case "ordered-list":
      return (
        <ol
          key={index}
          className={cn(
            "my-4 list-decimal space-y-1.5 pl-5 marker:text-muted-foreground",
            node.className,
          )}
          style={node.style}
        >
          {renderChildren()}
        </ol>
      );

    case "list": {
      const isOrderedList = node.listType === "number";
      const ListTag = (
        isOrderedList ? "ol" : "ul"
      ) as keyof JSX.IntrinsicElements;

      return (
        <ListTag
          key={index}
          className={cn(
            isOrderedList
              ? "my-4 list-decimal space-y-1.5 pl-5 marker:text-muted-foreground"
              : "my-4 list-disc space-y-1.5 pl-5 marker:text-muted-foreground",
            node.className,
          )}
          style={node.style}
        >
          {renderChildren()}
        </ListTag>
      );
    }

    case "list-item":
    case "listitem":
      return (
        <li
          key={index}
          className={cn(
            "min-w-0 mb-3 text-base leading-relaxed break-words",
            node.className,
          )}
          style={node.style}
        >
          {renderChildren({ inListItem: true })}
        </li>
      );

    case "blockquote":
      return (
        <blockquote
          key={index}
          className={cn(
            "my-6 border-l-2 border-border pl-4 italic text-foreground lg:pl-6",
            node.className,
          )}
          style={node.style}
        >
          {renderChildren()}
        </blockquote>
      );
    case "divider":
    case "horizontal-rule":
    case "horizontalrule":
    case "hr":
      return (
        <hr
          key={index}
          className={cn("my-8 border-border/80", node.className)}
          style={node.style}
        />
      );
    case "code-block":
      return (
        <pre
          key={index}
          className={cn(
            "my-6 overflow-x-auto rounded-sm bg-muted px-4 py-3 text-sm text-foreground",
            node.className,
          )}
          style={node.style}
        >
          <code>{node.content}</code>
        </pre>
      );

    case "link":
      {
        const href = getLinkHref(node.fields);
        const { relationTo, slug, valueID } = getLinkDocMeta(node.fields);

        return (
          <a
            key={index}
            href={href}
            target={node.fields?.newTab ? "_blank" : undefined}
            rel={node.fields?.newTab ? "noopener noreferrer" : undefined}
            data-lexical-link-id={node.id}
            data-lexical-link-type={node.fields?.linkType}
            data-lexical-link-relation-to={relationTo}
            data-lexical-link-doc-id={valueID}
            data-lexical-link-doc-slug={slug}
            className={cn(
              "decoration-primary underline underline-offset-4 transition-colors hover:text-foreground",
              node.className,
            )}
            style={node.style}
          >
            {renderChildren()}
          </a>
        );
      }

    case "upload": {
      const url = node.fields?.url;
      if (!url) return null;

      const alt = node.fields?.alt || "";
      const mimeType = node.fields?.mimeType || "";
      const filename =
        decodeHtmlEntities(node.fields?.filename) ?? "Download file";
      const isImage = mimeType.startsWith("image/");

      if (isImage) {
        const uploadHeightClass = getUploadHeightClass(
          node.fields?.imageHeight,
        );
        return (
          <Image
            key={index}
            src={url}
            alt={alt}
            width={node.fields?.width || 1600}
            height={node.fields?.height || 900}
            className={cn(
              uploadHeightClass,
              "mb-6 w-full rounded-lg object-cover",
              node.className,
            )}
            sizes="100vw"
            style={node.style}
          />
        );
      }

      return (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "decoration-primary underline underline-offset-4 transition-colors hover:text-foreground",
            node.className,
          )}
          style={node.style}
        >
          {filename}
        </a>
      );
    }
    case "block": {
      const blockType = node.fields?.blockType;
      if (
        blockType !== "embedded-contact-form" &&
        blockType !== "embedded-contact-form-section"
      ) {
        return null;
      }

      return (
        <EmbeddedPipedriveFormNode
          key={index}
          title={node.fields?.title}
          webformsUrl={node.fields?.webformsUrl}
          className={node.className}
          style={node.style}
        />
      );
    }

    default:
      // Fallback: render any nested children
      return <>{renderChildren()}</>;
  }
};

export const RichText = ({
  data,
  content,
  enableGutter,
  enableProse,
  className = "",
  colorMode,
  defaultSpanDisplay = "inline",
}: RichTextProps): JSX.Element | null => {
  void enableGutter;
  void enableProse;
  const resolvedContent = data ?? content;
  const defaultSpanClass = getDefaultSpanClass(defaultSpanDisplay);
  const rootClassName = cn(richTextRootClass, className);
  const rootStyle: RichTextStyle | undefined =
    colorMode === "dark"
      ? { "--arrow-right-color": 'url("/ArrowRightDarkLight.svg")' }
      : colorMode === "light"
        ? { "--arrow-right-color": 'url("/ArrowRightDark.svg")' }
        : undefined;

  if (hasRichTextNodes(resolvedContent)) {
    const nodes = resolvedContent.richTextNodes;
    if (nodes.length === 0) return null;

    return (
      <div className={rootClassName} style={rootStyle}>
        {nodes.map((node, index) => renderNode(node, index, defaultSpanClass))}
      </div>
    );
  }

  if (hasLexicalRoot(resolvedContent)) {
    const nodes = resolvedContent.root.children ?? [];
    if (nodes.length === 0) return null;

    return (
      <div className={rootClassName} style={rootStyle}>
        {nodes.map((node, index) => renderNode(node, index, defaultSpanClass))}
      </div>
    );
  }

  // Fallback: render arbitrary ReactNode
  return (
    <div className={rootClassName} style={rootStyle}>
      {resolvedContent as JSX.Element}
    </div>
  );
};

export default RichText;
